import { Helmet } from 'react-helmet-async'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Breadcrumbs,
  BreadcrumbsItemProps,
  Icon,
  Spinner,
  Tabs,
  useDialog,
} from '@sportsyou/react-dom-ui'

import { Colors } from '@sportsyou/core'
import { ContentContainer } from 'apps/web/src/styles/global-styles'

import CampaignEditTab from './TabEdit'
import styled from 'styled-components'
import useCampaignPortal from '../UseCampaignPortal'
import CampaignMetricsTab from './TabMetrics'
import { HandleNavigationProps } from '../../../types'

interface CampaignDetailScreenProps {
  campaignId?: string
}

export default function CampaignDetailScreen(props: CampaignDetailScreenProps) {
  const { sendConfirm } = useDialog()
  const navigate = useNavigate()
  const params = useParams()

  const {
    currentCampaign: campaign,
    isLoading,
    loadCampaign,
    loadSponsors,
    pathCampaignDetail,
    pathCampaigns,
  } = useCampaignPortal()

  const [hasChanges, setHasChanges] = useState(false)

  const activeTab = useMemo(() => {
    if (params.tab === 'edit') {
      return 1
    } else {
      return 0
    }
  }, [params.tab])

  const onChangeTabs = useCallback(
    (index: number) => {
      if (campaign?.id) {
        if (index === 1) {
          navigate(pathCampaignDetail(campaign, 'edit'))
        } else {
          navigate(pathCampaignDetail(campaign, 'metrics'))
        }
      }
    },
    [campaign, navigate, pathCampaignDetail]
  )

  const handleNavigation = useCallback(
    ({
      event,
      shouldPreventDefault,
      to,
      confirmOptions,
    }: HandleNavigationProps) => {
      if (shouldPreventDefault) {
        event?.preventDefault()
      }

      if (hasChanges && confirmOptions) {
        sendConfirm({
          isDestructive: true,
          confirmText: 'Ok',
          ...confirmOptions,
          onConfirm: () => {
            if (to) {
              navigate(to)
            } else if (confirmOptions.onConfirm) {
              confirmOptions.onConfirm()
            }
          },
        })
      } else {
        if (to) {
          navigate(to)
        } else if (confirmOptions?.onConfirm) {
          confirmOptions.onConfirm()
        }
      }
    },
    [hasChanges]
  )

  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbsItemProps[] = [
      {
        style: {
          padding: 10,
          borderRadius: '50%',
        },
        icon: <Icon fill={Colors.BLACK} name='Home' size={18} />,
        onClick: (event) => {
          handleNavigation({
            event,
            to: pathCampaigns(),
            confirmOptions: {
              message: 'You have unsaved changes. Do you really want to leave?',
            },
          })
        },
        title: 'Campaigns Portal',
      },
    ]

    let text: string | React.ReactNode = 'Create a Campaign'

    if (campaign?.id) {
      text = campaign?.campaignName ?? ''
    } else if (props.campaignId || params.id) {
      text = <Spinner size={24} />
    }

    items.push({
      style: {
        borderRadius: 20,
        paddingLeft: 12,
        paddingRight: 12,
        color: Colors.BLACK,
        textDecoration: 'none',
      },
      text,
    })

    return items
  }, [campaign, handleNavigation, pathCampaigns, props.campaignId, params.id])

  useEffect(() => {
    loadSponsors()
  }, [])

  const handleOnEdit = useCallback((isEdited: boolean) => {
    setHasChanges(isEdited)
  }, [])

  useEffect(() => {
    if (params.id) {
      loadCampaign(params.id)
    }
  }, [props.campaignId ?? params.id])

  return (
    <ContentContainer>
      <Helmet>
        <title>sportsYou - Campaign Editor</title>
      </Helmet>
      <Breadcrumbs items={breadcrumbItems} />

      {params.id ? (
        <Tabs
          confirmBeforeNavigate={{
            state: hasChanges,
            options: {
              confirmText: 'Ok',
              message: 'You have unsaved changes. Do you really want to leave?',
              isDestructive: true,
              onConfirm: () => {
                setHasChanges(false)
              },
            },
          }}
          contentStyle={tabsContentStyle}
          onChange={onChangeTabs}
          tabIndex={activeTab}
        >
          <Tabs.Item title='Metrics'>
            <CampaignMetricsTab campaign={campaign} />
          </Tabs.Item>
          <Tabs.Item title='Edit'>
            <CampaignEditTab
              campaignId={params.id}
              hasChanges={hasChanges}
              onEdit={handleOnEdit}
            />
          </Tabs.Item>
        </Tabs>
      ) : (
        <CampaignEditTab onEdit={handleOnEdit} hasChanges={hasChanges} />
      )}
    </ContentContainer>
  )
}

const tabsContentStyle = {
  padding: '20px 0',
}

const NavButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`
